import { IonPage } from '@ionic/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import moment from 'moment-timezone'
import { Responsive } from 'react-grid-layout'
import { useParams } from 'react-router-dom'

import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import {
  EventsCard,
  EventsKpiCard,
  PinUnitButton,
  ScheduleCancellationButton,
  UNIT_DETAILS_GRID_CONFIG,
  UNIT_EVENTS_GRID_CONFIG,
  UNIT_GRID_LAYOUT,
  UNIT_MANAGE_GRID_CONFIG,
  UnitDetailsCard,
  UnitManageContainer,
  UnitNotesNavigateButton,
  UnitStatus
} from '@apps-common/apps/portal/pages/units/pages/manage/components'
import { PullRefresh } from '@apps-common/components/common'
import { Header, LayoutContainer, LayoutPadding, LayoutXPaddingWrapper } from '@apps-common/components/layout'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_ACTIVE, UNIT_STATUS_CANCELLED, UNIT_STATUS_INACTIVE } from '@apps-common/config/unit-filter'
import { ROUTE_UNIT_MANAGE } from '@apps-radionet-portal/config/private-routes'

import { PageTitle } from '@apps-common/components/common/portal-title'
import { motionSlideDownFadeIn } from '@apps-common/config/motion-variants'
import { SizeContext, SizeProvider } from '@apps-common/contexts'
import { NotesCard } from './components/notes'
import { SignalStrengthCard, SignalStrengthKpiCard, UNIT_SIGNAL_STRENGTH_GRID_CONFIG } from './components/signal-strength'

const ResponsiveGridLayout = Responsive

export const UnitManagePage: React.FC = () => {
  const params = useParams()

  const { status, unitId } = params as { status: string; unitId: string }

  const { data: unit, isLoading: unitIsLoading, isError: unitIsError, refetch } = useGetUnitGenericDetails({ status, unitId })

  const icons: any = (
    <div className='flex items-center justify-end gap-x-[10px]'>
      {status !== UNIT_STATUS_CANCELLED.value && (
        <>
          <PinUnitButton loading={unitIsLoading} unit={unit} />
          <ScheduleCancellationButton loading={unitIsLoading} unit={unit} refetchCallback={refetch} />
        </>
      )}
      <UnitNotesNavigateButton loading={unitIsLoading} unit={unit} />
    </div>
  )

  const buildTitleText = () => {
    if (unit?.status === UNIT_STATUS_CANCELLED.value) {
      const date = moment(unit?.dateDeleted).local().format('ddd Do MMM YY')

      return (
        <span className='text-[0.75rem] sm:text-[0.8rem] leading-[0.5rem] jost flex items-center justify-start gap-[5px] text-on-error bg-error ring-1 ring-error pr-3 py-0 rounded-full mt-[5px]'>
          <div className='bg-on-error rounded-full p-[5px] flex items-center justify-center'>
            <commonIcons.toastError className='text-[16px] text-error' />
          </div>
          Cancelled on <span className='jost-medium italic'>{date}</span>
        </span>
      )
    }

    if (unit?.status === UNIT_STATUS_ACTIVE.value && unit?.cancelDate) {
      const date = moment(unit?.cancelDate).local().format('ddd Do MMM YY')

      return (
        <span className='text-[0.75rem] sm:text-[0.8rem] leading-[0.5rem] jost flex items-center justify-start gap-[5px] text-on-warning bg-warning ring-1 ring-warning pr-3 py-0 rounded-full mt-[5px]'>
          <div className='bg-on-warning rounded-full p-[5px] flex items-center justify-center'>
            <commonIcons.warning className='text-[16px] text-warning' />
          </div>
          Cancellation scheduled for <span className='jost-medium italic'>{date}</span>
        </span>
      )
    }

    if (unit?.status === UNIT_STATUS_INACTIVE.value) {
      return (
        <span className='text-[0.75rem] sm:text-[0.8rem] leading-[0.5rem] jost flex items-center justify-start gap-[5px] text-on-info bg-info ring-1 ring-info pr-3 py-0 rounded-full mt-[5px]'>
          <div className='bg-on-info rounded-full p-[5px] flex items-center justify-center'>
            <commonIcons.info className='text-[16px] text-info' />
          </div>
          Some options are disabled until activated.
        </span>
      )
    }

    return null
  }

  const titleText = buildTitleText()
  const loading = unitIsLoading || unitIsError

  return (
    <IonPage>
      <Header>
        <Navbar showBack transparent icons={icons} />
        <Breadcrumbs route={ROUTE_UNIT_MANAGE} />
        <div className='flex flex-col items-start sm:flex-row sm:items-center justify-between gap-1 pt-[5px] w-full mx-auto max-w-[420px] sm:!max-w-[870px] md:!max-w-[1450px] xl:!max-w-[1550px]'>
          <PageTitle classes='!leading-[1em] mt-[10px] '>Device Management</PageTitle>
          <UnitStatus unit={unit} />
        </div>
        <AnimatePresence>
          {titleText && (
            <motion.div
              key='unit-manage-unit-active-title-text'
              className='mt-2 mx-auto flex items-center justify-center sm:justify-start'
              layout
              {...motionSlideDownFadeIn(0.25)}
            >
              <div className='sm:inline-block min-w-[100%] sm:min-w-0'>{titleText}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </Header>
      <LayoutContainer>
        <LayoutPadding
          topPadding={classNames(
            '!px-0 sm:!px-[1rem] lg:!px-[1.4rem] xl:!px-[2rem] sm:max-w-[870px] md:max-w-[1680px] !pb-0',
            !titleText && 'pt-[36px] sm:pt-[60px] md:pt-[52px] xl:pt-[22px]',
            titleText && '!pt-[80px] sm:!pt-[110px] md:!pt-[90px] lg:!pt-[80px] xl:!pt-[60px]'
          )}
        >
          <PullRefresh loading={loading} callback={refetch} />
          <div className='sm:bg-surface-2 sm:border sm:border-border rounded-[30px] lg:pl-4 sm:max-w-[870px] md:max-w-[1590px]'>
            <AnimatePresence>
              <SizeProvider>
                <SizeContext.Consumer>
                  {({ width }) => {
                    if (!width) return null

                    return (
                      <div>
                        <div className='grid sm:hidden mx-auto pt-4 sm:gap-4'>
                          <LayoutXPaddingWrapper classes='mb-2 max-w-[420px]'>
                            <NotesCard status={status} unitId={unitId} classes='' />
                          </LayoutXPaddingWrapper>
                          <LayoutXPaddingWrapper classes='mb-2 max-w-[420px] mr-0'>
                            <UnitManageContainer layoutPrefix='mobile' unit={unit} />
                          </LayoutXPaddingWrapper>
                          <LayoutXPaddingWrapper classes='bg-surface-2 border-t border-b border-border py-4 mb-4'>
                            <UnitDetailsCard status={status} unit={unit} loading={loading} classes='max-w-[420px] mx-auto sm:max-w-auto' />
                          </LayoutXPaddingWrapper>
                          <LayoutXPaddingWrapper classes='max-w-[420px]'>
                            <div className='grid grid-cols-2 gap-[10px]'>
                              <SignalStrengthKpiCard unit={unit} />
                              <EventsKpiCard unit={unit} />
                            </div>
                          </LayoutXPaddingWrapper>
                        </div>
                        <div className='relative lg:left-[-15px] hidden sm:block w-full'>
                          <motion.div className='rounded-3xl '>
                            <ResponsiveGridLayout
                              // onBreakpointChange={(b) => console.log(b, size.width)}
                              width={width}
                              useCSSTransforms={false}
                              margin={{
                                xxl: [15, 15],
                                xl: [20, 20],
                                lg: [16, 16],
                                md: [15, 15],
                                sm: [15, 15],
                                xs: [12, 12],
                                xxs: [10, 10]
                              }}
                              layouts={UNIT_GRID_LAYOUT}
                              breakpoints={{ xxl: 1400, xl: 1312, lg: 1112, md: 946, sm: 856, xs: 607, xxs: 0 }}
                              cols={{ xxl: 12, xl: 12, lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                            >
                              <div key={UNIT_MANAGE_GRID_CONFIG.id}>
                                <div className='relative w-full h-full flex flex-col gap-[10px] lg:gap-[20px] overflow-hidden'>
                                  <div className='hidden sm:block grow'>
                                    <NotesCard status={status} unitId={unitId} classes='' />
                                  </div>
                                  <div className='max-w-full'>
                                    <UnitManageContainer layoutPrefix='desktop' unit={unit} />
                                  </div>
                                </div>
                              </div>
                              <div key={UNIT_DETAILS_GRID_CONFIG.id}>
                                <UnitDetailsCard status={status} unit={unit} loading={loading} />
                              </div>
                              <div key={UNIT_EVENTS_GRID_CONFIG.id}>
                                <EventsCard unit={loading ? undefined : unit} parentLoading={loading} />
                              </div>
                              <div key={UNIT_SIGNAL_STRENGTH_GRID_CONFIG.id}>
                                <SignalStrengthCard unit={unit} />
                              </div>
                            </ResponsiveGridLayout>
                          </motion.div>
                        </div>
                      </div>
                    )
                  }}
                </SizeContext.Consumer>
              </SizeProvider>
            </AnimatePresence>
          </div>
        </LayoutPadding>
      </LayoutContainer>
    </IonPage>
  )
}
